import { neighborsFor } from '../../game/geometry';
import type { BoardSize } from '../../shared/config';
import type { PowerUpSpawn } from '../../shared/power-ups';
import { BOARD_GAP } from './board-hit-testing';

/** A word traces in over most of its showing time; the rest briefly holds the finished word. */
const TRACE_SHARE = 0.8;

export interface EncounteredPickup {
  tile: number;
  id: string;
}

/** SVG polyline points (0–100 viewBox) through the centres of `path`'s tiles. */
export function tracePoints(path: readonly number[], size: number): string {
  const cell = (1 - (size - 1) * BOARD_GAP) / size;
  const centre = (position: number) => (position * (cell + BOARD_GAP) + cell / 2) * 100;
  return path.map((index) => `${centre(index % size)},${centre(Math.floor(index / size))}`).join(' ');
}

/**
 * The leading tiles of a shown word's path, advancing one tile at a time like a player's trace.
 * `progress` is 0–1 through the time the word is shown (post-match recap, tutorial loop).
 */
export function tracedTiles(path: readonly number[], progress: number): readonly number[] {
  const reached = Math.floor(traceCompletion(progress) * path.length) + 1;
  return path.slice(0, Math.min(path.length, reached));
}

/** 0–1 completion of a shown word's trace; paces anything that finishes with the trace. */
export function traceCompletion(progress: number): number {
  return Math.min(1, progress / TRACE_SHARE);
}

/** Advance only on a new adjacent tile; spawn claims use the same visible list as the board. */
export function advanceTrace(
  path: readonly number[],
  pickups: readonly EncounteredPickup[],
  index: number,
  size: BoardSize,
  visibleSpawns: readonly Readonly<PowerUpSpawn>[],
): { path: number[]; pickups: readonly EncounteredPickup[]; feedback: 'select' | 'backtrack' } | null {
  if (index < 0 || index >= size * size) return null;
  const previous = path[path.length - 1];
  if (previous === index) return null;
  if (path.length > 1 && path[path.length - 2] === index) {
    return {
      path: path.slice(0, -1),
      pickups: pickups.filter((pickup) => pickup.tile !== previous),
      feedback: 'backtrack',
    };
  }
  if (path.includes(index)) return null;
  if (previous !== undefined && !neighborsFor(size)[previous]?.includes(index)) return null;
  const spawn = visibleSpawns.find((candidate) => candidate.tile === index);
  return {
    path: [...path, index],
    pickups: spawn ? [...pickups, { tile: index, id: spawn.id }] : pickups,
    feedback: 'select',
  };
}
